import { SimpleGrid } from "@mantine/core";
import debounce from "lodash/debounce";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FeatureToggle } from "#app/featureToggle";
import { colors } from "#app/theme/v1/colors";
import classes from "#modules/quoteRequestLines/css/QuoteRequestLineBatchRightSection.module.css";
import { PartCostDisplay } from "#modules/quoteRequestLines/PartCostDisplay";
import { QuoteRequestLineFormData } from "#modules/quoteRequestLines/types/QuoteRequestLineFormData";
import { useUpdateMaterial } from "#modules/quoteRequestLines/useUpdateMaterial";
import { useDeleteQuoteRequestLine } from "#modules/quoteRequestLines/view/useDeleteQuoteRequestLine";
import { useSetBatchReferenceLine } from "#modules/quoteRequestLines/view/useSetBatchReferenceLine";
import { useUpdateQuoteRequestLineQuantity } from "#modules/quoteRequestLines/view/useUpdateQuoteRequestLineQuantity";
import { WaitingForDataState } from "#modules/quoteRequestLines/views/QuoteRequestLineRightSection/WaitingForDataState";
import { useAssembliesContext } from "#modules/quoteRequests/context/AssembliesContext";
import { Icon } from "#shared/icons/Icon";
import { ActionIcon } from "#shared/view/components/ActionIcon";
import { Box } from "#shared/view/components/Box";
import { Flex } from "#shared/view/components/Flex";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { NumberInput } from "#shared/view/designSystem/inputs/NumberInput";
import { Skeleton } from "#shared/view/designSystem/Skeleton";
import { Tooltip } from "#shared/view/designSystem/Tooltip";
import { Features } from "#types/FeatureToggleTypes";
import {
  AssemblyWithChildren,
  getBatchReferenceLineId,
  hasValidDataToPredict,
  PartTypeSchema,
  PartTypeType,
  QuoteRequestLineDto,
} from "shared";

type QuoteRequestLineBatchRightSectionProps = {
  assembliesInBatch: AssemblyWithChildren<QuoteRequestLineDto>[];
  partType: PartTypeType | null;
  openDetailsPanelForQuoteRequestLine: (quoteRequestLineId: string) => void;
  isPredicting: boolean;
  hasChildren: boolean;
};

export const QuoteRequestLineBatchRightSection = ({
  assembliesInBatch,
  partType,
  openDetailsPanelForQuoteRequestLine,
  isPredicting,
  hasChildren,
}: QuoteRequestLineBatchRightSectionProps) => {
  const { control } = useFormContext<QuoteRequestLineFormData>();
  const { t } = useTranslation();

  const { activeAssembliesByBatch, setActiveAssemblyForBatch, resetActiveAssembliesForBatch } =
    useAssembliesContext();

  const materialGrade = useWatch({ control, name: "materialGrade" });
  const materialName = useWatch({ control, name: "materialName" });

  const { updateMaterial } = useUpdateMaterial();
  const { updateQuoteRequestLineQuantity } = useUpdateQuoteRequestLineQuantity();
  const { deleteQuoteRequestLine } = useDeleteQuoteRequestLine();

  const linesInBatch = assembliesInBatch.map((assembly) => assembly.quoteRequestLine);
  const referenceLineId = getBatchReferenceLineId(linesInBatch);
  const { setBatchReferenceLine, isPending: isSettingBatchReferenceLine } =
    useSetBatchReferenceLine({
      quoteRequestLineIdsInBatch: linesInBatch.map((line) => line.id),
    });

  const onHandleRetryPrediction =
    (assembly: AssemblyWithChildren<QuoteRequestLineDto>) => async () => {
      await updateMaterial({
        quoteRequestLineId: assembly.quoteRequestLine.id,
        predictionStatus: assembly.quoteRequestLine.predictionStatus,
        payload: {
          materialGrade,
          materialName,
        },
      });
    };

  const onHandleChangeQty = (quoteRequestLineId: string) => (value: number) => {
    const currentQuantity = assembliesInBatch.find(
      (assembly) => assembly.quoteRequestLine.id === quoteRequestLineId,
    )?.quoteRequestLine.quantity;

    if (value === currentQuantity) {
      return;
    }

    updateQuoteRequestLineQuantity({
      id: quoteRequestLineId,
      payload: {
        quantity: value,
      },
    });
  };

  const onChangeQtyDebounced = (quoteRequestLineId: string) =>
    debounce(onHandleChangeQty(quoteRequestLineId), 1000);

  const hasValidPredictionData = () => {
    const hasValidData = hasValidDataToPredict({
      materialGrade,
      isAssembly: partType === PartTypeSchema.enum.ASSEMBLY,
      children: assembliesInBatch.map((assembly) => assembly.quoteRequestLine),
    });

    return hasValidData;
  };

  const isActive = (assembly: AssemblyWithChildren<QuoteRequestLineDto>, index: number) => {
    return (
      activeAssembliesByBatch[assembly.quoteRequestLine.partId] === assembly.quoteRequestLine.id ||
      (!activeAssembliesByBatch[assembly.quoteRequestLine.partId] &&
        index === assembliesInBatch.length - 1)
    );
  };

  const onHandleClick =
    (assembly: AssemblyWithChildren<QuoteRequestLineDto>) => (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      const isInputClick = target.closest("input");

      setActiveAssemblyForBatch(assembly.quoteRequestLine.partId, assembly.quoteRequestLine.id);

      if (!isInputClick) {
        openDetailsPanelForQuoteRequestLine(assembly.quoteRequestLine.id);
      }
    };

  return (
    <Flex gap={5} style={{ width: "100%" }} align="stretch">
      <Box
        style={{
          width: 100,
          flex: "0 0 100px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          marginTop: 30,
        }}
      >
        <Text
          style={{
            textAlign: "right",
            flex: 1,
          }}
        >
          {t("qrl.quantity")}
        </Text>
        <Text style={{ textAlign: "right", flex: 1 }}>{t("qrl.price.estimatedCost")}</Text>
      </Box>

      <SimpleGrid
        cols={Math.max(1, assembliesInBatch.length)}
        spacing={5}
        style={{ flex: "1 1 auto" }}
      >
        {assembliesInBatch.map((assembly, idx) => {
          const active = isActive(assembly, idx);
          const displayLoader = active && isPredicting;
          const isReferenceLine = assembly.quoteRequestLine.id === referenceLineId;

          return (
            <Box
              key={assembly.quoteRequestLine.id}
              onClick={onHandleClick(assembly)}
              pos="relative"
              style={{
                "--batch-container-bg": active ? colors.blue[1] : undefined,
                "--batch-container-hover-bg": active ? colors.blue[1] : colors.blue[0],
              }}
              className={classes.batchContainer}
            >
              <Box pos="absolute" top={0} right={0}>
                <ActionIcon
                  size="sm"
                  variant="transparent"
                  aria-label={t("qrl.batch.delete")}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    resetActiveAssembliesForBatch(assembly.quoteRequestLine.partId);
                    deleteQuoteRequestLine(assembly.quoteRequestLine.id);
                  }}
                >
                  <Icon name="x" size={16} color={colors.gray[5]} />
                </ActionIcon>
              </Box>

              <NumberInput
                label={
                  <Flex align="center" gap={4}>
                    <span>{t("qrl.batch.label", { index: idx + 1 })}</span>
                    <FeatureToggle feature={Features.COSTING_OPTIMIZATION}>
                      <Tooltip
                        label={
                          isReferenceLine ? t("qrl.batch.reference") : t("qrl.batch.setReference")
                        }
                      >
                        <ActionIcon
                          size="sm"
                          variant="transparent"
                          aria-label={t("qrl.batch.setReference")}
                          disabled={isSettingBatchReferenceLine}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            if (isReferenceLine) {
                              return;
                            }
                            setBatchReferenceLine({ id: assembly.quoteRequestLine.id });
                          }}
                        >
                          <Icon
                            name={isReferenceLine ? "starFill" : "star"}
                            size={14}
                            color={isReferenceLine ? colors.blue[5] : colors.gray[5]}
                          />
                        </ActionIcon>
                      </Tooltip>
                    </FeatureToggle>
                  </Flex>
                }
                styles={{
                  label: { fontWeight: active ? "600" : "400" },
                  input: { width: "auto", minWidth: "100%", maxWidth: 100 },
                }}
                value={assembly.quoteRequestLine.quantity}
                onChange={onChangeQtyDebounced(assembly.quoteRequestLine.id)}
                autoFocus={active}
                disabled={isPredicting}
              />

              <Box
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "end",
                }}
              >
                {displayLoader && (
                  <Box my={4}>
                    <Stack gap={4} align="flex-end">
                      <Skeleton height={25} width={70} />
                      <Skeleton height={12} width={90} />
                    </Stack>
                  </Box>
                )}
                {!displayLoader &&
                  (hasValidPredictionData() ? (
                    <PartCostDisplay
                      quoteRequestLine={assembly.quoteRequestLine}
                      openDetailsPanel={() => null}
                      retryPrediction={onHandleRetryPrediction(assembly)}
                      condensed
                      hasChildren={hasChildren}
                    />
                  ) : (
                    <WaitingForDataState />
                  ))}
              </Box>
            </Box>
          );
        })}
      </SimpleGrid>
    </Flex>
  );
};
